/**
 * Helpers that turn stored foods/meals into the nutrient values the UI shows.
 */
import { addNutrients, NUTRIENTS, roundNutrient, scaleNutrients } from '@engine/nutrients';
import type { Food, FoodAiFill, MealItem, MealLog, NutrientMap, NutrientSourceMap } from './types';

export interface EffectiveNutrients {
  /** Per 100 g. */
  values: NutrientMap;
  sources: NutrientSourceMap;
  /** How many of the 94 catalog nutrients are known. */
  known: number;
}

/** Measured values first; an AI fill only supplies what is still missing. */
export function effectiveNutrients(food: Food, fill: FoodAiFill | null): EffectiveNutrients {
  const values: NutrientMap = { ...food.nutrients };
  const sources: NutrientSourceMap = { ...food.nutrient_sources };
  const fallback: NutrientSourceMap[string] = food.source === 'user' ? 'user' : food.source === 'usda_branded' ? 'label' : 'usda';
  for (const k of Object.keys(values)) if (!sources[k]) sources[k] = fallback;
  if (fill) {
    for (const [k, v] of Object.entries(fill.nutrients)) {
      if (values[k] === undefined) { values[k] = v; sources[k] = 'ai'; }
    }
  }
  return { values, sources, known: Object.keys(values).length };
}

export const TOTAL_NUTRIENTS: number = NUTRIENTS.length;

/** Keys the food still lacks, in Master Sheet order. */
export function missingKeys(values: NutrientMap): string[] {
  return NUTRIENTS.map((n) => n.key).filter((k) => values[k] === undefined);
}

/** The basic macro fields every meal item has, as catalog keys. */
export function basicItemNutrients(i: Pick<MealItem, 'calories' | 'protein_g' | 'carbs_g' | 'fat_g' | 'fiber_g' | 'sugar_g' | 'alcohol_g' | 'sodium_mg' | 'potassium_mg'>): NutrientMap {
  const out: NutrientMap = { energy_kcal: i.calories, protein_g: i.protein_g, carbs_g: i.carbs_g, fat_g: i.fat_g };
  if (i.fiber_g !== undefined) out.fiber_g = i.fiber_g;
  if (i.sugar_g !== undefined) out.sugars_g = i.sugar_g;
  if (i.alcohol_g !== undefined) out.alcohol_g = i.alcohol_g;
  if (i.sodium_mg !== undefined) out.sodium_mg = i.sodium_mg;
  if (i.potassium_mg !== undefined) out.potassium_mg = i.potassium_mg;
  return out;
}

/** Full nutrients for one item: its linked-food profile when present, otherwise the basics. */
export function itemNutrients(i: MealItem): NutrientMap {
  return i.nutrients && Object.keys(i.nutrients).length > 0 ? i.nutrients : basicItemNutrients(i);
}

export function roundMap(m: NutrientMap): NutrientMap {
  const out: NutrientMap = {};
  for (const [k, v] of Object.entries(m)) out[k] = roundNutrient(v);
  return out;
}

/** Totals for a meal: its stored full profile, or the basic columns for older meals. */
export function mealNutrients(m: MealLog): NutrientMap {
  if (m.nutrients && Object.keys(m.nutrients).length > 0) return m.nutrients;
  const basic: NutrientMap = { energy_kcal: m.calories, protein_g: m.protein_g, carbs_g: m.carbs_g, fat_g: m.fat_g };
  if (m.fiber_g !== null) basic.fiber_g = m.fiber_g;
  if (m.sugar_g !== null) basic.sugars_g = m.sugar_g;
  if (m.alcohol_g !== null) basic.alcohol_g = m.alcohol_g;
  if (m.sodium_mg !== null) basic.sodium_mg = m.sodium_mg;
  if (m.potassium_mg !== null) basic.potassium_mg = m.potassium_mg;
  return basic;
}

export function dayNutrients(meals: ReadonlyArray<MealLog>): NutrientMap {
  return addNutrients(...meals.map(mealNutrients));
}

/** Builds a diary item for `grams` of a food. */
export function itemFromFood(food: Food, per100: NutrientMap, grams: number, quantity: number, unit: string): MealItem {
  const n: NutrientMap = roundMap(scaleNutrients(per100, grams));
  const g = (k: string): number => n[k] ?? 0;
  return {
    name: food.name,
    food_id: food.id,
    quantity,
    unit,
    grams: Math.round(grams * 10) / 10,
    preparation: '',
    calories: Math.round(g('energy_kcal') * 10) / 10,
    protein_g: g('protein_g'),
    carbs_g: g('carbs_g'),
    fat_g: g('fat_g'),
    fiber_g: g('fiber_g'),
    sugar_g: g('sugars_g'),
    alcohol_g: g('alcohol_g'),
    sodium_mg: g('sodium_mg'),
    potassium_mg: g('potassium_mg'),
    nutrients: n,
  };
}

export const SOURCE_BADGE: Readonly<Record<string, { label: string; title: string }>> = {
  usda: { label: 'USDA', title: 'Measured by USDA FoodData Central' },
  calc: { label: 'Calc', title: 'Calculated from other USDA values' },
  ai: { label: 'AI', title: 'Estimated by AI. Treat as an approximation' },
  user: { label: 'You', title: 'Entered by you' },
  label: { label: 'Label', title: 'From a nutrition label' },
};

export const FOOD_SOURCE_LABEL: Readonly<Record<string, string>> = {
  usda_foundation: 'USDA Foundation',
  usda_sr_legacy: 'USDA SR Legacy',
  usda_branded: 'USDA Branded',
  usda_survey: 'USDA FNDDS',
  user: 'My food',
  ai: 'My food · AI',
  recipe: 'My recipe',
  barcode: 'My food · barcode',
};
